import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Drawer,
  Empty,
  Grid,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useLiveopsChange } from '@/hooks/useLiveopsChange';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import {
  countryFlag,
  countryName,
  dayHeading,
  groupByIstDay,
  istClock,
  istDayKey,
  istDayStart,
} from '@/utils/cricketCalendar';
import type {
  CalendarFestival,
  CricketCalendarSettings,
  CricketFixture,
  CricketSeries,
  CricketSyncStatus,
} from '@/types/matchPushes';
import { CalendarSettingsCard } from './CalendarSettingsCard';
import { FestivalFormModal } from './FestivalFormModal';
import { FixtureDrawer } from './FixtureDrawer';
import { FixtureFormModal } from './FixtureFormModal';
import { FestivalRow, FixtureRow } from './FixtureRow';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 100;

/** The windows the list can show, as days from the start of today (IST). */
const WINDOWS = {
  next14: { label: 'Next 2 weeks', fromDays: 0, toDays: 14 },
  next30: { label: 'Next 30 days', fromDays: 0, toDays: 30 },
  next90: { label: 'Next 90 days', fromDays: 0, toDays: 90 },
  next365: { label: 'Next 12 months', fromDays: 0, toDays: 365 },
  past7: { label: 'Last 7 days', fromDays: 7, toDays: 1 },
} as const;
type WindowKey = keyof typeof WINDOWS;

const syncLine = (status: CricketSyncStatus | null): string => {
  if (!status) return '';
  if (!status.lastRunAt) return 'Not synced yet';
  const summary = status.lastSummary ? ` · ${status.lastSummary}` : '';
  return `Synced ${istClock(status.lastRunAt)} IST${summary}`;
};

/**
 * Live Ops → Cricket Calendar: the matches pulled from Sportskeeda (plus any
 * added by hand) by IST day, which of them are tracked for match pushes,
 * festivals, and a side panel per match.
 */
export const CricketCalendarPage: React.FC = () => {
  const screens = useBreakpoint();
  const wide = Boolean(screens.lg);

  const [trackedOnly, setTrackedOnly] = useState(true);
  const [country, setCountry] = useState<string | undefined>();
  const [seriesSlug, setSeriesSlug] = useState<string | undefined>();
  const [showFestivals, setShowFestivals] = useState(true);
  const [windowKey, setWindowKey] = useState<WindowKey>('next14');

  const [items, setItems] = useState<CricketFixture[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [festivals, setFestivals] = useState<CalendarFestival[]>([]);
  const [seriesList, setSeriesList] = useState<CricketSeries[]>([]);
  const [syncStatus, setSyncStatus] = useState<CricketSyncStatus | null>(null);
  const [settings, setSettings] = useState<CricketCalendarSettings | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingIds, setSavingIds] = useState<Record<string, boolean>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [fixtureForm, setFixtureForm] = useState<{ open: boolean; fixture: CricketFixture | null }>(
    { open: false, fixture: null }
  );
  const [festivalForm, setFestivalForm] = useState<{
    open: boolean;
    festival: CalendarFestival | null;
  }>({ open: false, festival: null });

  const requestId = useRef(0);
  const { canChange, deniedTitle } = useLiveopsChange();

  const range = useMemo(() => {
    const w = WINDOWS[windowKey];
    const todayStart = istDayStart(istDayKey(Date.now())).getTime();
    return {
      from: new Date(todayStart - w.fromDays * DAY_MS),
      to: new Date(todayStart + w.toDays * DAY_MS),
    };
  }, [windowKey]);

  const fixtureQuery = useCallback(
    (pageNo: number) => ({
      from: range.from.toISOString(),
      to: range.to.toISOString(),
      tracked: trackedOnly ? ('true' as const) : undefined,
      country,
      series: seriesSlug,
      page: pageNo,
      limit: PAGE_SIZE,
    }),
    [range, trackedOnly, country, seriesSlug]
  );

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const day = (d: Date) => istDayKey(d.getTime());
      const [fixtures, festivalRes, seriesRes] = await Promise.all([
        adminApi.cricketCalendar.listFixtures(fixtureQuery(1)),
        showFestivals
          ? adminApi.cricketCalendar.listFestivals({ from: day(range.from), to: day(range.to) })
          : Promise.resolve(null),
        adminApi.cricketCalendar.listSeries({
          from: range.from.toISOString(),
          to: range.to.toISOString(),
        }),
      ]);
      if (id !== requestId.current) return;
      setItems(fixtures.data.items);
      setTotal(fixtures.data.total);
      setPage(1);
      setFestivals(festivalRes?.data.items ?? []);
      setSeriesList(seriesRes.data.items);
    } catch (error) {
      if (id === requestId.current) {
        message.error(apiErrorMessage(error, 'Failed to load the calendar'));
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [fixtureQuery, showFestivals, range]);

  useEffect(() => {
    load();
  }, [load]);

  const loadSyncStatus = useCallback(async () => {
    try {
      setSyncStatus((await adminApi.cricketCalendar.getSyncStatus()).data);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to load the sync status'));
    }
  }, []);

  useEffect(() => {
    loadSyncStatus();
    adminApi.cricketCalendar
      .getSettings()
      .then((r) => setSettings(r.data))
      .catch((error) => message.error(apiErrorMessage(error, 'Failed to load the settings')));
  }, [loadSyncStatus]);

  const loadMore = async () => {
    // Same guard as load(): a filter change while this is in flight makes the answer stale.
    const id = requestId.current;
    setLoadingMore(true);
    try {
      const response = await adminApi.cricketCalendar.listFixtures(fixtureQuery(page + 1));
      if (id !== requestId.current) return;
      setItems((prev) => {
        const known = new Set(prev.map((f) => f.id));
        return [...prev, ...response.data.items.filter((f) => !known.has(f.id))];
      });
      setTotal(response.data.total);
      setPage((p) => p + 1);
    } catch (error) {
      if (id === requestId.current) {
        message.error(apiErrorMessage(error, 'Failed to load more matches'));
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleTracked = async (fixture: CricketFixture, tracked: boolean) => {
    setSavingIds((s) => ({ ...s, [fixture.id]: true }));
    try {
      const response = await adminApi.cricketCalendar.updateFixture(fixture.id, { tracked });
      const saved = response.data;
      setItems((prev) =>
        prev.map((f) => (f.id === fixture.id ? (saved?.id ? saved : { ...f, tracked }) : f))
      );
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to change tracking'));
    } finally {
      setSavingIds((s) => ({ ...s, [fixture.id]: false }));
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const response = await adminApi.cricketCalendar.syncNow();
      message.success(response.data.summary || 'Sync done');
      await Promise.all([load(), loadSyncStatus()]);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Sync failed'));
      loadSyncStatus();
    } finally {
      setSyncing(false);
    }
  };

  const days = useMemo(
    () => groupByIstDay(items, showFestivals ? festivals : []),
    [items, festivals, showFestivals]
  );
  const selected = items.find((f) => f.id === selectedId) ?? null;

  const countryOptions = useMemo(() => {
    const codes = new Set<string>(settings?.trackedCountries ?? []);
    items.forEach((f) => f.audienceCountries.forEach((c) => codes.add(c)));
    if (country) codes.add(country);
    return [...codes].sort().map((c) => ({
      value: c,
      label: `${countryFlag(c)} ${countryName(c)}`.trim(),
    }));
  }, [settings, items, country]);

  const seriesOptions = useMemo(
    () =>
      seriesList.map((s) => ({
        value: s.seriesSlug,
        label: `${s.seriesName} (${s.fixtures})`,
      })),
    [seriesList]
  );

  const detail = selected && (
    <FixtureDrawer
      fixture={selected}
      canChange={canChange}
      deniedTitle={deniedTitle}
      onEdit={() => setFixtureForm({ open: true, fixture: selected })}
      onClose={wide ? () => setSelectedId(null) : undefined}
    />
  );

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        Cricket Calendar
      </Title>
      <Text type="secondary">
        Matches come from Sportskeeda's schedule once a day; admins can add or fix any match. Live
        events pick their matches from here.
      </Text>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          margin: '12px 0',
        }}
      >
        <Segmented
          value={trackedOnly ? 'tracked' : 'all'}
          onChange={(v) => setTrackedOnly(v === 'tracked')}
          options={[
            { value: 'tracked', label: 'Tracked only' },
            { value: 'all', label: trackedOnly ? 'Show all' : `Show all (${total})` },
          ]}
        />
        <Select
          allowClear
          showSearch
          placeholder="Country"
          style={{ minWidth: 150 }}
          value={country}
          onChange={setCountry}
          options={countryOptions}
          optionFilterProp="label"
        />
        <Select
          allowClear
          showSearch
          placeholder="Series"
          style={{ minWidth: 220 }}
          value={seriesSlug}
          onChange={setSeriesSlug}
          options={seriesOptions}
          optionFilterProp="label"
        />
        <Select
          value={windowKey}
          onChange={setWindowKey}
          style={{ minWidth: 140 }}
          options={(Object.keys(WINDOWS) as WindowKey[]).map((k) => ({
            value: k,
            label: WINDOWS[k].label,
          }))}
        />
        <Checkbox checked={showFestivals} onChange={(e) => setShowFestivals(e.target.checked)}>
          Festivals
        </Checkbox>
        <Space style={{ marginLeft: 'auto' }} wrap>
          <Text type="secondary">{syncLine(syncStatus)}</Text>
          {syncStatus && !syncStatus.enabled && <Text type="warning">Daily sync is off</Text>}
          {syncStatus && syncStatus.lastSuccess === false && (
            <Text type="danger">last sync failed</Text>
          )}
          {syncStatus && (
            <Text type="secondary">
              · calls today {syncStatus.callsToday}/{syncStatus.callCap}
            </Text>
          )}
          <Tooltip
            title={
              deniedTitle ??
              (syncStatus && !syncStatus.canSyncNow
                ? 'Allowed again 10 minutes after the last run'
                : 'Fetch the schedule now')
            }
          >
            <Button
              icon={<SyncOutlined />}
              loading={syncing}
              disabled={!canChange || !syncStatus || !syncStatus.canSyncNow}
              onClick={syncNow}
            >
              Sync now
            </Button>
          </Tooltip>
          <Tooltip title={deniedTitle}>
            <Button
              disabled={!canChange}
              onClick={() => setFestivalForm({ open: true, festival: null })}
            >
              + Add festival
            </Button>
          </Tooltip>
          <Tooltip title={deniedTitle}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canChange}
              onClick={() => setFixtureForm({ open: true, fixture: null })}
            >
              Add match by hand
            </Button>
          </Tooltip>
        </Space>
      </div>

      <Row gutter={20} align="top">
        <Col xs={24} lg={16} xl={17}>
          <Spin spinning={loading}>
            {days.length === 0 && !loading ? (
              <Empty
                description={
                  trackedOnly
                    ? 'No tracked matches in this window. Try Show all.'
                    : 'No matches in this window.'
                }
              />
            ) : (
              days.map((entry) => (
                <div key={entry.day} style={{ marginBottom: 14 }}>
                  <Text
                    type="secondary"
                    strong
                    style={{
                      display: 'block',
                      margin: '0 0 6px',
                      textTransform: 'uppercase',
                      fontSize: 13,
                    }}
                  >
                    {dayHeading(entry.day)}
                  </Text>
                  {entry.festivals.map((festival) => (
                    <FestivalRow
                      key={festival.id}
                      festival={festival}
                      canChange={canChange}
                      onEdit={() => setFestivalForm({ open: true, festival })}
                    />
                  ))}
                  {entry.fixtures.map((fixture) => (
                    <FixtureRow
                      key={fixture.id}
                      fixture={fixture}
                      selected={fixture.id === selectedId}
                      saving={Boolean(savingIds[fixture.id])}
                      canChange={canChange}
                      onSelect={() => setSelectedId(fixture.id)}
                      onToggleTracked={(tracked) => toggleTracked(fixture, tracked)}
                    />
                  ))}
                </div>
              ))
            )}
            {items.length < total && (
              <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
                <Button loading={loadingMore} onClick={loadMore}>
                  Load more ({items.length} of {total})
                </Button>
              </div>
            )}
          </Spin>
        </Col>
        {wide && (
          <Col lg={8} xl={7}>
            <div style={{ position: 'sticky', top: 12 }}>
              {detail ?? (
                <Alert type="info" showIcon message="Select a match to see its details." />
              )}
            </div>
          </Col>
        )}
      </Row>

      {!wide && (
        <Drawer
          open={Boolean(selected)}
          onClose={() => setSelectedId(null)}
          width={360}
          title="Match"
          destroyOnClose
        >
          {detail}
        </Drawer>
      )}

      <div style={{ marginTop: 16 }}>
        <CalendarSettingsCard settings={settings} onSaved={setSettings} />
      </div>

      <FixtureFormModal
        open={fixtureForm.open}
        fixture={fixtureForm.fixture}
        onClose={() => setFixtureForm({ open: false, fixture: null })}
        onSaved={(saved) => {
          setFixtureForm({ open: false, fixture: null });
          if (saved) setSelectedId(saved.id);
          else setSelectedId(null);
          load();
        }}
      />
      <FestivalFormModal
        open={festivalForm.open}
        festival={festivalForm.festival}
        onClose={() => setFestivalForm({ open: false, festival: null })}
        onSaved={() => {
          setFestivalForm({ open: false, festival: null });
          load();
        }}
      />
    </div>
  );
};

export default CricketCalendarPage;
